import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { FileJson, Store, CheckCircle2, AlertCircle, Loader2, Database, Layers, UtensilsCrossed, Settings2, ChefHat, Link2, MessageCircle, ArrowRight, ArrowLeft, UserCircle, KeyRound, Mail, RefreshCw } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────
interface RestaurantPayload {
  name: string;
  slug: string;
  whatsapp_number?: string;
  [key: string]: any;
}
interface ParsedPayload {
  restaurant: RestaurantPayload;
  categories: any[];
  items: any[];
  options: any[];
  stations: any[];
}
interface CreateResult {
  success: boolean;
  result?: {
    success: boolean;
    restaurant_id: number;
    restaurant_slug: string;
    menu_import: {
      success: boolean;
      restaurant_id: number;
      categories_inserted: number;
      items_inserted: number;
      options_inserted: number;
    };
    stations_created: number;
    station_mappings_created: number;
  };
}
interface ValidationState {
  status: 'idle' | 'valid' | 'error';
  errors: string[];
  counts: {
    categories: number;
    items: number;
    options: number;
    stations: number;
  };
}
// ─── Summary Card ────────────────────────────────────────────
function SummaryMetric({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return <div className="bg-[#0B0B0B] border border-white/5 rounded-xl p-4 text-center" data-id="element-3040">
      <Icon className="w-4 h-4 text-white/30 mx-auto mb-2" data-id="element-3041" />
      <div className="text-2xl font-bold text-white" data-id="element-3042">{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1" data-id="element-3043">
        {label}
      </div>
    </div>;
}
// ─── Page Component ──────────────────────────────────────────
export function EnrollmentRestaurantPage() {
  const navigate = useNavigate();
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<ParsedPayload | null>(null);
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
    errors: [],
    counts: {
      categories: 0,
      items: 0,
      options: 0,
      stations: 0
    }
  });
  const [restaurantFields, setRestaurantFields] = useState({
    name: '',
    slug: '',
    whatsapp_number: ''
  });
  const [adminFields, setAdminFields] = useState({
    full_name: '',
    email: '',
    temporary_password: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const specials = '!@#$%';
    let pwd = '';
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    pwd += specials.charAt(Math.floor(Math.random() * specials.length));
    setAdminFields(prev => ({
      ...prev,
      temporary_password: pwd
    }));
  };
  // ─── Validate ────────────────────────────────────────────
  const handleValidate = () => {
    setParsedData(null);
    setCreateResult(null);
    setCreateError(null);
    const errors: string[] = [];
    if (!jsonInput.trim()) {
      setValidation({
        status: 'error',
        errors: ['El JSON está vacío.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    let data: any;
    try {
      data = JSON.parse(jsonInput);
    } catch {
      setValidation({
        status: 'error',
        errors: ['El JSON no es válido. Revisa la sintaxis.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    // Structural validation
    if (!data.restaurant || typeof data.restaurant !== 'object' || Array.isArray(data.restaurant)) {
      errors.push('"restaurant" debe ser un objeto.');
    } else {
      if (!data.restaurant.name) errors.push('"restaurant.name" es obligatorio.');
      if (!data.restaurant.slug) errors.push('"restaurant.slug" es obligatorio.');
    }
    if (!Array.isArray(data.categories)) {
      errors.push('"categories" debe ser un array.');
    } else if (data.categories.length === 0) {
      errors.push('"categories" no puede estar vacío.');
    }
    if (!Array.isArray(data.items)) {
      errors.push('"items" debe ser un array.');
    } else if (data.items.length === 0) {
      errors.push('"items" no puede estar vacío.');
    }
    if (!Array.isArray(data.options)) {
      errors.push('"options" debe ser un array.');
    }
    if (!Array.isArray(data.stations)) {
      errors.push('"stations" debe ser un array.');
    } else if (data.stations.length === 0) {
      errors.push('"stations" no puede estar vacío.');
    }
    if (errors.length > 0) {
      setValidation({
        status: 'error',
        errors,
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    const counts = {
      categories: data.categories.length,
      items: data.items.length,
      options: data.options.length,
      stations: data.stations.length
    };
    setParsedData(data as ParsedPayload);
    setRestaurantFields({
      name: data.restaurant.name || '',
      slug: data.restaurant.slug || '',
      whatsapp_number: data.restaurant.whatsapp_number || ''
    });
    setValidation({
      status: 'valid',
      errors: [],
      counts
    });
  };
  // ─── Create ──────────────────────────────────────────────
  const handleCreateRestaurant = async (parsedJsonPayload: ParsedPayload) => {
    setIsCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      console.log('[EnrollmentRestaurant] Sending payload:', parsedJsonPayload);
      // Get user session for Authorization
      const {
        data: {
          session
        },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError) {
        setCreateError(`Error obteniendo sesión: ${sessionError.message}`);
        setIsCreating(false);
        return;
      }
      if (!session?.access_token) {
        setCreateError('Tu sesión expiró. Inicia sesión nuevamente en /admin/login');
        setIsCreating(false);
        return;
      }
      console.log('[EnrollmentRestaurant] Session:', {
        hasSession: true,
        accessToken: 'present',
        userId: session.user?.id ?? null
      });
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-restaurant-from-menu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(parsedJsonPayload)
      });
      const data = await response.json();
      console.log('[EnrollmentRestaurant] Response:', {
        status: response.status,
        data
      });
      if (!response.ok) {
        console.error('[EnrollmentRestaurant] Backend error:', data);
        const details = Array.isArray(data?.details) && data.details.length > 0 ? `\n\nDetalles:\n- ${data.details.join('\n- ')}` : '';
        setCreateError((data?.error || data?.message || `Error del servidor (${response.status})`) + details);
        return;
      }
      if (!data?.success) {
        setCreateError(data?.error || data?.details || 'Error al crear el restaurante.');
        return;
      }
      setCreateResult(data as CreateResult);
    } catch (error: any) {
      console.error('[EnrollmentRestaurant] Fetch error:', error);
      setCreateError(error instanceof Error ? error.message : 'Ocurrió un error inesperado al crear el restaurante.');
    } finally {
      setIsCreating(false);
    }
  };
  // ─── Render ──────────────────────────────────────────────
  if (createResult?.success && createResult.result) {
    const res = createResult.result;
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-3044">
        <div className="max-w-2xl w-full space-y-8 text-center" data-id="element-3045">
          <div className="w-24 h-24 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-3046">
            <CheckCircle2 className="w-12 h-12 text-[#00a870]" data-id="element-3047" />
          </div>
          <div data-id="element-3048">
            <h1 className="text-3xl font-black tracking-tight" data-id="element-3049">
              ¡Restaurante Creado!
            </h1>
            <p className="text-white/50 mt-2" data-id="element-3050">
              El restaurante y su menú han sido configurados exitosamente.
            </p>
          </div>

          <div className="bg-[#141414] border border-[#006847]/30 rounded-2xl p-6 text-left space-y-6" data-id="element-3051">
            <div className="flex items-center justify-between pb-4 border-b border-white/5" data-id="element-3052">
              <div data-id="element-3053">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-3054">
                  Restaurante
                </p>
                <p className="text-xl font-bold text-white mt-1" data-id="element-3055">
                  {restaurantFields.name}
                </p>
              </div>
              <div className="text-right" data-id="element-3056">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-3057">
                  Slug
                </p>
                <p className="text-lg font-mono text-[#00a870] mt-1" data-id="element-3058">
                  {res.restaurant_slug}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-3059">
              <SummaryMetric icon={Layers} label="Categorías" value={res.menu_import.categories_inserted} data-id="element-3060" />
              <SummaryMetric icon={UtensilsCrossed} label="Productos" value={res.menu_import.items_inserted} data-id="element-3061" />
              <SummaryMetric icon={Settings2} label="Opciones" value={res.menu_import.options_inserted} data-id="element-3062" />
              <SummaryMetric icon={ChefHat} label="Estaciones" value={res.stations_created} data-id="element-3063" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4" data-id="element-3064">
            <button onClick={() => {
            setCreateResult(null);
            setJsonInput('');
            setParsedData(null);
            setValidation({
              status: 'idle',
              errors: [],
              counts: {
                categories: 0,
                items: 0,
                options: 0,
                stations: 0
              }
            });
          }} className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold transition-colors" data-id="element-3065">
              Crear otro
            </button>
            <button onClick={() => navigate(`/admin/${res.restaurant_slug}`)} className="px-8 py-4 bg-[#006847] hover:bg-[#005a3d] text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2" data-id="element-3066">
              Ver restaurante <ArrowRight className="w-5 h-5" data-id="element-3067" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-[#006847] selection:text-white" data-id="element-3068">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8" data-id="element-3069">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-3070">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors shrink-0 mt-2" data-id="element-3071">
            <ArrowLeft className="w-5 h-5" data-id="element-3072" />
          </button>
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-3073">
            <Store className="w-7 h-7 text-[#006847]" data-id="element-3074" />
          </div>
          <div data-id="element-3075">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-3076">
              Crear restaurante desde menú
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-3077">
              Pega el JSON generado por el parser para crear restaurante, menú y
              KDS automáticamente a través de la Edge Function.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-3078">
          {/* ─── Left: JSON Input ─── */}
          <div className="lg:col-span-7 space-y-6" data-id="element-3079">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20" data-id="element-3080">
              <div className="flex items-center justify-between mb-4" data-id="element-3081">
                <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-3082">
                  <FileJson className="w-5 h-5 text-white/40" data-id="element-3083" />
                  Payload JSON
                </h2>
                <button onClick={handleValidate} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition-all active:scale-95" data-id="element-3084">
                  Validar JSON
                </button>
              </div>

              <textarea value={jsonInput} onChange={e => setJsonInput(e.target.value)} placeholder="Pega aquí el JSON generado por el sistema..." className="w-full h-[420px] bg-[#0B0B0B] border border-white/10 rounded-xl p-4 font-mono text-[13px] leading-relaxed text-white/80 placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 resize-y transition-colors" spellCheck={false} data-id="element-3085" />

              {/* Validation Error */}
              {validation.status === 'error' && <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3086">
                  <div className="flex items-center gap-2 text-red-400 mb-2" data-id="element-3087">
                    <AlertCircle className="w-4 h-4" data-id="element-3088" />
                    <strong className="text-sm font-semibold" data-id="element-3089">
                      Errores de validación
                    </strong>
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-300/80 space-y-1" data-id="element-3090">
                    {validation.errors.map((err, i) => <li key={i} data-id="element-3091">{err}</li>)}
                  </ul>
                </div>}
            </div>
          </div>

          {/* ─── Right: Preview & Actions ─── */}
          <div className="lg:col-span-5 space-y-6" data-id="element-3092">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 sticky top-8" data-id="element-3093">
              <h2 className="text-base font-semibold mb-6 flex items-center gap-2" data-id="element-3094">
                <Store className="w-5 h-5 text-white/40" data-id="element-3095" />
                Datos del Restaurante
              </h2>

              <div className="space-y-5" data-id="element-3096">
                {/* Editable Fields */}
                <div data-id="element-3097">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3098">
                    Nombre
                  </label>
                  <input type="text" value={restaurantFields.name} onChange={e => setRestaurantFields(prev => ({
                  ...prev,
                  name: e.target.value
                }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Ej: Capricho de Canela" data-id="element-3099" />
                </div>

                <div data-id="element-3100">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3101">
                    Slug (URL)
                  </label>
                  <div className="flex relative" data-id="element-3102">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-white/10 bg-white/5 text-white/40 text-sm" data-id="element-3103">
                      noizacr.com/
                    </span>
                    <input type="text" value={restaurantFields.slug} onChange={e => setRestaurantFields(prev => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-')
                  }))} disabled={validation.status !== 'valid'} className="flex-1 bg-[#0B0B0B] border border-white/10 rounded-r-xl px-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="capricho-de-canela" data-id="element-3104" />
                  </div>
                </div>

                <div data-id="element-3105">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3106">
                    WhatsApp (Opcional)
                  </label>
                  <div className="relative" data-id="element-3107">
                    <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3108" />
                    <input type="text" value={restaurantFields.whatsapp_number} onChange={e => setRestaurantFields(prev => ({
                    ...prev,
                    whatsapp_number: e.target.value
                  }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="50688888888" data-id="element-3109" />
                  </div>
                </div>

                {/* ─── Admin del Restaurante ─── */}
                <div className="pt-6 border-t border-white/10" data-id="element-3110">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-white/70" data-id="element-3111">
                    <UserCircle className="w-4 h-4 text-white/30" data-id="element-3112" />
                    Admin del restaurante
                  </h3>

                  <div className="space-y-4" data-id="element-3113">
                    <div data-id="element-3114">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3115">
                        Nombre completo
                      </label>
                      <input type="text" value={adminFields.full_name} onChange={e => setAdminFields(prev => ({
                      ...prev,
                      full_name: e.target.value
                    }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Juan Pérez" data-id="element-3116" />
                    </div>

                    <div data-id="element-3117">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3118">
                        Correo electrónico
                      </label>
                      <div className="relative" data-id="element-3119">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3120" />
                        <input type="email" value={adminFields.email} onChange={e => setAdminFields(prev => ({
                        ...prev,
                        email: e.target.value
                      }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="juan@email.com" data-id="element-3121" />
                      </div>
                    </div>

                    <div data-id="element-3122">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3123">
                        Contraseña temporal
                      </label>
                      <div className="flex gap-2" data-id="element-3124">
                        <div className="relative flex-1" data-id="element-3125">
                          <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3126" />
                          <input type="text" value={adminFields.temporary_password} onChange={e => setAdminFields(prev => ({
                          ...prev,
                          temporary_password: e.target.value
                        }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Noiza1234!" data-id="element-3127" />
                        </div>
                        <button type="button" onClick={generatePassword} disabled={validation.status !== 'valid'} className="px-3 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors disabled:opacity-50 shrink-0" title="Generar contraseña" data-id="element-3128">
                          <RefreshCw className="w-4 h-4 text-white/50" data-id="element-3129" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counts Summary */}
                {validation.status === 'valid' && <div className="pt-6 border-t border-white/10" data-id="element-3130">
                    <div className="flex items-center gap-2 text-[#00a870] mb-4" data-id="element-3131">
                      <CheckCircle2 className="w-4 h-4" data-id="element-3132" />
                      <span className="text-sm font-bold" data-id="element-3133">JSON Válido</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-3134">
                      <SummaryMetric icon={Layers} label="Categorías" value={validation.counts.categories} data-id="element-3135" />
                      <SummaryMetric icon={UtensilsCrossed} label="Productos" value={validation.counts.items} data-id="element-3136" />
                      <SummaryMetric icon={Settings2} label="Opciones" value={validation.counts.options} data-id="element-3137" />
                      <SummaryMetric icon={ChefHat} label="Estaciones" value={validation.counts.stations} data-id="element-3138" />
                    </div>
                  </div>}

                {/* Create Error */}
                {createError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3139">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3140" />
                    <div className="text-sm whitespace-pre-line" data-id="element-3141">
                      {createError}
                    </div>
                  </div>}

                {/* Action Button */}
                <button onClick={() => {
                if (!parsedData) return;
                if (validation.status !== 'valid') {
                  handleValidate();
                  return;
                }
                const payloadToSend = {
                  ...parsedData,
                  restaurant: {
                    ...parsedData.restaurant,
                    name: restaurantFields.name,
                    slug: restaurantFields.slug,
                    whatsapp_number: restaurantFields.whatsapp_number
                  },
                  admin_user: adminFields.email ? {
                    full_name: adminFields.full_name,
                    email: adminFields.email,
                    temporary_password: adminFields.temporary_password
                  } : undefined
                };
                handleCreateRestaurant(payloadToSend as any);
              }} disabled={validation.status !== 'valid' || isCreating} className="w-full py-4 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-3142">
                  {isCreating ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-3143" />
                      Creando restaurante...
                    </> : <>
                      <Store className="w-5 h-5" data-id="element-3144" />
                      Crear restaurante
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}